/**
 * Shared row chrome state for all repeatable section tables (#996 / #1154).
 * Menus and drag handles stay visible; interactivity is gated via disabled flags.
 */

/**
 * Whether the trailing compact actions/overflow-menu column should be reserved
 * (#999). Read-only lists (e.g. the Devices browse view) never show an overflow
 * menu, so on the compact layout (native mobile AND narrow/mobile web) we collapse
 * the column and hand the space back to the data columns. Wide desktop web renders
 * the `desktop` layout (never compact here), so it is unaffected. Shared by every
 * device table row + header so the six device types stay in lockstep without
 * per-type edits.
 */
export function repeatableCompactActionsColumnReserved(params: {
  isCompact: boolean;
  readOnly?: boolean;
  hasOpenAction?: boolean;
}): boolean {
  if (!params.isCompact) {
    return false;
  }
  const collapse = Boolean(params.readOnly) && !params.hasOpenAction;
  return !collapse;
}

export function repeatableRowMenuState(params: {
  disabled?: boolean;
  readOnly?: boolean;
  isPlaceholder?: boolean;
  canEdit: boolean;
  canDuplicate: boolean;
  canDelete: boolean;
}): { showOverflowMenu: boolean; menuDisabled: boolean } {
  const showOverflowMenu =
    !params.isPlaceholder && (!params.readOnly || params.canEdit);
  const menuDisabled =
    Boolean(params.disabled) ||
    (!params.canEdit && !params.canDuplicate && !params.canDelete);
  return { showOverflowMenu, menuDisabled };
}

export function repeatableRowDragState(params: {
  reorderEnabled?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  isPlaceholder?: boolean;
  rowIndex?: number | null;
}): { showDragHandle: boolean; dragHandleActive: boolean } {
  // Honest chrome (#999 Story 3.7): only show the handle when reorder is live.
  // `reorderEnabled` already encodes readOnly / allowReorder / itemCount gating.
  const dragHandleActive =
    Boolean(params.reorderEnabled) &&
    !params.disabled &&
    !params.isPlaceholder &&
    params.rowIndex != null;
  return { showDragHandle: dragHandleActive, dragHandleActive };
}

/**
 * Reserve the drag column only when reorder is live (#999 Story 3.9).
 * Avoids an empty grip spacer when handles are honest-hidden.
 */
export function repeatableSectionShowDragColumn(
  reorderActive: boolean,
): boolean {
  return reorderActive;
}
